/**
 * Axios Retry Interceptor
 * 
 * Automatically retries failed requests caused by transient issues:
 * - Network errors (no response received)
 * - Server errors: 502 Bad Gateway, 503 Service Unavailable, 504 Gateway Timeout
 * - Request timeouts (ECONNABORTED)
 * 
 * Does NOT retry:
 * - 4xx client errors (400, 401, 403, 404, 422) — these are intentional responses
 * - POST/PUT/DELETE by default (only GET/HEAD/OPTIONS are idempotent)
 * - Requests that already exhausted retries
 */

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000; // 1s base, doubles each retry (1s → 2s)

const RETRYABLE_STATUS_CODES = new Set([502, 503, 504, 520, 521, 522, 524]);
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options']);
// Login/auth endpoints are safe to retry even as POST (no side effects on failure)
const SAFE_RETRY_PATHS = ['/auth/login', '/auth/me'];

function isRetryable(error) {
  // Network error — no response at all (connection refused, DNS failure, etc.)
  if (!error.response && error.code !== 'ERR_CANCELED') return true;

  // Timeout
  if (error.code === 'ECONNABORTED') return true;

  // Retryable server errors (includes Cloudflare 520/521/522/524)
  if (error.response && RETRYABLE_STATUS_CODES.has(error.response.status)) return true;

  return false;
}

function shouldRetry(error, config) {
  if (!isRetryable(error)) return false;

  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  
  // Retry idempotent methods (GET, HEAD, OPTIONS) always
  // Also retry POST for safe paths (login, auth check) 
  const isSafePost = method === 'post' && SAFE_RETRY_PATHS.some(p => url.includes(p));
  if (!IDEMPOTENT_METHODS.has(method) && !isSafePost) return false;

  // Check retry count
  const retryCount = config.__retryCount || 0;
  return retryCount < MAX_RETRIES;
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Attach retry interceptor to an axios instance.
 * Call this BEFORE any auth/error interceptors so retries happen first.
 * 
 * @param {import('axios').AxiosInstance} axiosInstance 
 */
export function withRetry(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      if (!config || !shouldRetry(error, config)) {
        return Promise.reject(error);
      }

      config.__retryCount = (config.__retryCount || 0) + 1;
      const retryDelay = RETRY_DELAY_MS * Math.pow(2, config.__retryCount - 1);

      await delay(retryDelay);
      return axiosInstance.request(config);
    }
  );
}
